import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    CardBody,
    Alert,
    Button,
    Label,
    Input,
    Form,
    FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";

import UserService from "Api/Services/UserService";
import Spinners from "components/Common/Spinner";
import PageTitle from "../../../components/Common/PageTitle";

const UserEdit = () => {
    const params = useParams(); // Get the user ID from the URL
    const navigate = useNavigate();


    const imageUrl = `${process.env.REACT_APP_PROFILE_IMAGE_API_URL}`;
    const [status, setStatus] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [error, setError] = useState({});
    const [success, setSuccess] = useState(false);
    const [name, setName] = useState('');
    const [getPracticeName, setPracticeName] = useState('');
    const [username, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [getSpecialty, setSpecialty] = useState('');
    const [getQualification, setQualification] = useState('');
    const [isGroupLogin, setIsGroupLogin] = useState(false);
    const [currentDoctorGroupName, setCurrentDoctorGroupName] = useState('');
    const [currentDoctorGroupId, setCurrentDoctorGroupId] = useState('');
    const [doctorOptions, setDoctorOptions] = useState([]);
    const [selectedDoctorUserIds, setSelectedDoctorUserIds] = useState([]);
    const [previewDataImage, setPreviewDataImage] = useState(null);
    const [isLoading, setLoading] = useState(false);
    const [errorimage, setErrorimage] = useState(false);
    const [getFaxNumber, setFaxNumber] = useState('');

    const accountTitle = isGroupLogin ? "Shared Login" : "Doctor";
    const accountDescription = isGroupLogin
        ? "Edit the office login and choose which doctors it can access."
        : "Edit this individual provider account.";

    document.title = `Edit ${accountTitle} | Infuse One`;

    useEffect(() => {
        fetchUserData();
    }, [params.id]);

    useEffect(() => {
        if (isGroupLogin) {
            loadDoctorOptions();
        }
    }, [isGroupLogin]);

    const loadDoctorOptions = async () => {
        try {
            const response = await UserService.GetDoctorGroupMemberOptions().json();

            if (response.status) {
                const doctorAccounts = response.data?.doctor_accounts || [];
                setDoctorOptions(doctorAccounts.map(account => ({
                    value: account.id,
                    label: account.display_name || account.name,
                })));
            }
        } catch (loadError) {
            console.error(loadError);
        }
    };

    const fetchUserData = async () => {
        setLoading(true);
        try {
            const response = await UserService.GetUserById(params.id).json();

            if (response.status) {
                const userData = response.data;
                setName(userData.name);
                setPracticeName(userData.practicename);
                setUserName(userData.username);
                setEmail(userData.email);
                setPhoneNumber(userData.phone_number);
                setStatus(userData.is_active == 1 ? true : false);
                setPreviewDataImage(userData.profile_image ? imageUrl + '/' + userData.profile_image : null);
                setSpecialty(userData.specialty);
                setQualification(userData.qualification);
                setIsGroupLogin(Boolean(userData.is_group_login));
                setCurrentDoctorGroupId(userData.doctor_group?.id ? String(userData.doctor_group.id) : '');
                setCurrentDoctorGroupName(userData.doctor_group?.name || userData.doctor_group_name || '');
                setSelectedDoctorUserIds(userData.managed_doctor_user_ids || []);
		setFaxNumber(userData.faxnumber);
                setLoading(false);
            } else {
                setLoading(false);
                setError({ general: response.message });
            }
        } catch (error) {
            console.error(error);
            setError({ general: 'Something went wrong while fetching user data.' });
        }
    };

    const handleStatusChange = () => setStatus(!status);

    const handleImageChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            const fileType = file.type;
            const validImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

            if (validImageTypes.includes(fileType)) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewImage(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setErrorimage('Please upload a valid image.');
                return;
                // alert('Please upload a valid image file.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = {
            username: e.target.username.value,
            name,
            practicename: isGroupLogin ? name : getPracticeName,
            email: e.target.email.value,
            phone_number: phoneNumber,
            profile_image: previewImage ? previewImage : null,
            specialty: e.target.specialty ? e.target.specialty.value : '',
            qualification: e.target.qualification ? e.target.qualification.value : '',
            is_active: status == true ? 1 : 0,
	    faxnumber: e.target.faxnumber ? e.target.faxnumber.value : '',
            is_group_login: isGroupLogin ? 1 : 0,
        };

        if (isGroupLogin) {
            formData.doctor_group_id = currentDoctorGroupId ? Number(currentDoctorGroupId) : null;
            formData.create_doctor_group_name = name;
            formData.doctor_user_ids = selectedDoctorUserIds;
        }

        setLoading(true);
        try {
            const response = await UserService.UpdateUser(params.id, formData).json();
            if (response.status) {
                setSuccess(response.message);
                setLoading(false);
                navigate(`/user-list?view=${isGroupLogin ? 'shared-login' : 'doctor'}`);
                setErrorimage('');
            } else {
                setLoading(false);
                setError(response.errors);
            }
        } catch (error) {
            console.error(error);
            setError({ general: 'Something went wrong while updating user.' });
        }
    };

    const handleNameChange = (e) => {
        const regex = /^[A-Za-z\s]*$/;
        const newValue = e.target.value;
        if (regex.test(newValue)) {
            setName(newValue);
        }
    };

    const handlePracticeNameChange = (e) => {
        const regex = /^[A-Za-z\s]*$/;
        const newValue = e.target.value;
        if (regex.test(newValue)) {
            setPracticeName(newValue);
        }
    };

    const handlePhoneNumberChange = (e) => {
        const newValue = e.target.value;
        if (/^\d{0,12}$/.test(newValue)) {
            setPhoneNumber(newValue);
        }
    };

    const handleSpecialtyChange = (e) => {
        const regex = /^[A-Za-z\s]*$/;
        const newValue = e.target.value;
        if (regex.test(newValue)) {
            setSpecialty(newValue);
        }
    };

    const handleQualificationChange = (e) => {
        const regex = /^\d{0,10}$/; // Only digits allowed
        const newValue = e.target.value;
        if (regex.test(newValue)) {
            setQualification(newValue);
        }
    };
      
    const handleFaxnumberonChange = (e) => {
    	const regex = /^[0-9\s!@#$%^&*()_\-+=\[\]{}|\\;:'",.<>/?`~]*$/;
	const newValue= e.target.value;
	 if(regex.test(newValue)) {
		setFaxNumber(newValue);
	 }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <PageTitle title={`Edit ${accountTitle}`} />
                    {isLoading ? <Spinners setLoading={setLoading} /> :
                        <Card className="commonboxform">
                            <CardBody className="p-4">
                                <div className="mb-4">
                                    <h4 className="mb-1">Edit {accountTitle}</h4>
                                    <div className="text-muted">{accountDescription}</div>
                                </div>
                                {success && (
                                    <Alert color="success" style={{ marginTop: "13px" }}>
                                        {success}
                                    </Alert>
                                )}
                                {error.general && (
                                    <Alert color="danger" style={{ marginTop: "13px" }}>
                                        {error.general}
                                    </Alert>
                                )}

                                {/* <h4 className="card-title mb-4">Edit User</h4> */}

                                <Form className="form-horizontal" onSubmit={handleSubmit}>
                                    <div className="mb-4">
                                        <Label className="form-label">Account Type</Label>
                                        <div className="d-flex align-items-center gap-2">
                                            <span className={`badge ${isGroupLogin ? "bg-primary" : "bg-secondary"}`}>
                                                {isGroupLogin ? "Shared Login" : "Doctor"}
                                            </span>
                                            <span className="text-muted small">Account type is fixed after creation.</span>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <Label className="form-label">Username <i className="mdi mdi-asterisk text-danger asteriskiconsty"></i></Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            id="username"
                                            name="username"
                                            placeholder="Enter username"
                                            defaultValue={username}
                                        />
                                        {error.username && (
                                            <FormFeedback type="invalid" style={{ display: 'block' }}>{error.username[0]}</FormFeedback>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <Label className="form-label">{isGroupLogin ? "Shared Login Display Name" : "Provider Name"} <i className="mdi mdi-asterisk text-danger asteriskiconsty"></i>
                                        </Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            id="name"
                                            placeholder={isGroupLogin ? "Enter Shared Login Display Name" : "Enter Provider Name"}
                                            value={name}
                                            onChange={handleNameChange}
                                        />
                                        {error.name && (
                                            <FormFeedback type="invalid" style={{ display: 'block' }}>{error.name[0]}</FormFeedback>
                                        )}
                                    </div>
                                    {!isGroupLogin ? (
                                        <div className="mb-3">
                                            <Label className="form-label">Practice Name</Label>
                                            <Input
                                                type="text"
                                                className="form-control"
                                                id="practicename"
                                                name="practicename"
                                                placeholder="Enter Practice Name"
                                                value={getPracticeName}
                                                onChange={handlePracticeNameChange}
                                            />
                                            {error.practicename && (
                                                <FormFeedback type="invalid" style={{ display: 'block' }}>{error.practicename[0]}</FormFeedback>
                                            )}
                                        </div>
                                    ) : null}
                                    {isGroupLogin ? (
                                        <div className="mb-3">
                                            <Label className="form-label">Doctors this shared login can access</Label>
                                            <Select
                                                isMulti
                                                options={doctorOptions}
                                                value={doctorOptions.filter(option => selectedDoctorUserIds.includes(option.value))}
                                                onChange={selected => setSelectedDoctorUserIds(selected ? selected.map(option => option.value) : [])}
                                                placeholder="Choose doctors"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menu: base => ({ ...base, zIndex: 9999 }),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                }}
                                                closeMenuOnSelect={false}
                                            />
                                            <div className="text-muted small mt-2">
                                                {currentDoctorGroupName
                                                    ? `This shared login will update the ${currentDoctorGroupName} group automatically.`
                                                    : "This shared login will automatically create one doctor group for the doctors you choose here."}
                                            </div>
                                        </div>
                                    ) : null}
                                    <div className="mb-3">
                                        <Label className="form-label">Office Contact Email  <i className="mdi mdi-asterisk text-danger asteriskiconsty"></i></Label>
                                        <Input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            placeholder="Enter Office Contact Email "
                                            defaultValue={email} // Placeholder for existing email
                                            name="email" // Note: Also ensure email is passed in response for existing users
                                        />
                                        {error.email && (
                                            <FormFeedback type="invalid" style={{ display: 'block' }}>{error.email[0]}</FormFeedback>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <Label className="form-label">Phone Number <i className="mdi mdi-asterisk text-danger asteriskiconsty"></i></Label>
                                        <Input
                                            type="text"
                                            className="form-control"
                                            id="phone_number"
                                            placeholder="Enter phone number"
                                            onChange={handlePhoneNumberChange}
                                            value={phoneNumber}
                                        />
                                        {error.phone_number && (
                                            <FormFeedback type="invalid" style={{ display: 'block' }}>{error.phone_number[0]}</FormFeedback>
                                        )}
                                    </div>
                                    {!isGroupLogin ? (
                                        <>
                                            <div className="mb-3">
                                                <Label className="form-label">Specialty</Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="specialty"
                                                    name="specialty"
                                                    placeholder="Enter Specialty "
                                                    value={getSpecialty}
                                                    onChange={handleSpecialtyChange}
                                                />
                                                {error.specialty && (
                                                    <FormFeedback type="invalid" style={{ display: 'block' }}>{error.specialty[0]}</FormFeedback>
                                                )}
                                            </div>
                                            <div className="mb-3">
                                                <Label className="form-label">NPI </Label>
                                                <Input
                                                    type="text"
                                                    className="form-control"
                                                    id="qualification"
                                                    name="qualification"
                                                    placeholder="Enter NPI"
                                                    value={getQualification}
                                                    onChange={handleQualificationChange}
                                                />
                                                {error.qualification && (
                                                    <FormFeedback type="invalid" style={{ display: 'block' }}>{error.qualification[0]}</FormFeedback>
                                                )}
                                            </div>
			    		    <div className="mb-3">
			    			    <Label calssName="form-control">Fax number </Label>
			    			    <input 
			    				type="text"
			    				className="form-control"
			    				id="faxnumberid"
			    				name="faxnumber"
			    				placeholder="Enter fax number"
			    				value={getFaxNumber}
			    				onChange={handleFaxnumberonChange}
			    		 	    />
			    			    {error.faxnumber && (
							    <FormFeedback type="invalid" style={{ display: 'block' }}> {error.faxnumber[0]}</FormFeedback>
						    )}
			    		 </div>
                                            <div className="mb-3">
                                                <Label className="form-label">Profile Image</Label>
                                                {(previewDataImage || previewImage) && (
                                                    <div className="mb-3 text-left">
                                                        <img
                                                            src={previewDataImage ? previewDataImage : previewImage}
                                                            alt="Profile Preview"
                                                            className="img-fluid"
                                                            style={{ width: '100px', height: '60px', objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                )}
                                                <Input
                                                    type="file"
                                                    className="form-control"
                                                    id="profile_image"
                                                    accept="image/*"
                                                    onChange={handleImageChange}
                                                />
                                                {error.profile_image && (
                                                    <FormFeedback type="invalid" style={{ display: 'block' }}>{error.profile_image[0]}</FormFeedback>
                                                )}
                                                {errorimage && (
                                                    <FormFeedback type="invalid" style={{ display: 'block' }}>{errorimage}</FormFeedback>
                                                )}
                                            </div>
                                        </>
                                    ) : null}

                                    <div className="mb-3">
                                        <Label className="form-label">Status</Label>
                                        <div className="form-check form-switch form-switch-lg mb-3">
                                            <Input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="customSwitchsizelg"
                                                name="status"
                                                checked={status}
                                                onClick={handleStatusChange}
                                            />
                                            <Label className="form-check-label" htmlFor="customSwitchsizelg">
                                                {status ? 'Active' : 'Inactive'}
                                            </Label>
                                        </div>
                                    </div>

                                    <div className="text-left mt-4">
                                        <Button type="submit" color="secondary">
                                            Save {accountTitle}
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                    }
                </Container>
            </div>
        </React.Fragment >
    );
};

export default UserEdit;
